// External Imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import HCaptcha from '@hcaptcha/react-hcaptcha';

// Styles
import './CaptchaPasswordReset.css';

// Internal Imports
import { useRouteParamsContext } from 'src/viewLayer/components/contexts/RouteParamsContext';
import { useCaptchaConfigContext } from 'src/viewLayer/components/contexts/CaptchaConfigContext';
import { CAPTCHA_ERRORS, MAX_RETRY_COUNT } from 'src/dataLayer/localData/captcha.js';
import { reportCaptchaError } from 'src/dataLayer/remoteData/rollbar';
import { trackAuthenticationResetPassword } from 'src/dataLayer/remoteData/babbelEventStream';

import useHandleCaptchaPasswordResetRequest from './useHandleCaptchaPasswordResetRequest';

// Component Definition
function CaptchaPasswordReset({ setCaptchaToken, shouldExecuteCaptcha, shouldTrackTokenRequested = true }) {
  const { locale } = useRouteParamsContext();
  const { setCaptchaConfig } = useCaptchaConfigContext();
  const {
    makeCaptchaPasswordResetRequest,
    captchaConfig
  } = useHandleCaptchaPasswordResetRequest();
  const captchaRef = useRef(null);
  const [retryCount, setRetryCount] = useState(0);
  const isConfigAvailable = Boolean(captchaConfig?.provider && captchaConfig?.siteKey);

  useEffect(() => {
    if (captchaConfig?.siteKey) {
      setCaptchaConfig({ siteKey: captchaConfig.siteKey });
    }
  }, [captchaConfig, setCaptchaConfig]);

  const executeCaptcha = useCallback(() => {
    if (captchaRef.current) {
      captchaRef.current.resetCaptcha();
      captchaRef.current.execute('captcha-password-reset');
    }
  }, [captchaRef.current]);

  useEffect(() => {
    if (shouldExecuteCaptcha) {
      executeCaptcha();
    }
  }, [executeCaptcha, shouldExecuteCaptcha]);

  const onVerifyCaptcha = useCallback((token) => {
    if (token) {
      setCaptchaToken(token);
      trackAuthenticationResetPassword({ stage: 'captcha-success' });
    }
  }, [setCaptchaToken]);

  const onErrorCaptcha = useCallback((error) => {
    if (CAPTCHA_ERRORS.has(error) && retryCount < MAX_RETRY_COUNT) {
      setRetryCount((previousRetryCount) => previousRetryCount + 1);
      executeCaptcha();
      reportCaptchaError(
        `Error while verifying captcha. Number of retry attempts: ${retryCount + 1}`,
        new Error(error),
      );
      trackAuthenticationResetPassword({ stage: 'captcha-failure' });
    } else {
      reportCaptchaError('Error while retry verifying captcha:', new Error(error));
    }
  }, [retryCount]);

  const onExpireCaptcha = useCallback(() => {
    setCaptchaToken('');
    executeCaptcha();
  }, [setCaptchaToken]);

  useEffect(() => {
    if (!isConfigAvailable) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- We want this to happen in the background and not actively wait for the result
      (async () => {
        if (shouldTrackTokenRequested) {
          trackAuthenticationResetPassword({ stage: 'token-requested' });
        }
        await makeCaptchaPasswordResetRequest();
      })();
    }
  }, [isConfigAvailable, shouldTrackTokenRequested]);

  if (!captchaConfig) {
    return null;
  }

  return (
    <div className="hcaptcha-container">
      <HCaptcha
        id="captcha-password-reset"
        ref={captchaRef}
        sitekey={captchaConfig.siteKey}
        onVerify={onVerifyCaptcha}
        onError={onErrorCaptcha}
        onExpire={onExpireCaptcha}
        onChalExpired={onExpireCaptcha}
        size="invisible"
        languageOverride={locale === 'en_GB' ? 'en' : locale}
      />
    </div>
  );
}

export { CaptchaPasswordReset };
